import stringify from 'json-stringify-safe';
import { RequestBuilder } from '@cvent/nucleus-networking';
import debounce from 'lodash.debounce';

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

/**
 * By default, errors serialize to "{}" because all of their properties are non-enumerable. Work around this by copying
 * the non-enumerable properties on the error and its prototypes to another object where they will be enumerable.
 */

function formatError(err) {
  var result = {};
  var current = err;

  while (current && Object.getPrototypeOf(current) !== Object.prototype) {
    Object.getOwnPropertyNames(current).forEach(function (key) {
      if (typeof err[key] !== 'function') {
        result[key] = err[key];
      }
    });
    current = Object.getPrototypeOf(current);
  }

  return stringify(result);
} // Formats an arg. If it's an error, format errors, else safe-stringify


function formatIndividualArg(arg) {
  if (arg instanceof Error) {
    return formatError(arg);
  } else if (_typeof(arg) === 'object') {
    return stringify(arg);
  }

  return arg;
}
/**
 * Formats message to send to server
 * @param {String} marker - marker to be added before the log message
 * @param {String} message - the log message
 * @param {Array} args - other arguments to be added
 * @param {Object} context - the context the log message is associated with
 */


function formatMessage(marker, message, args, context) {
  return ["[".concat(marker, "]: ").concat(message)].concat(_toConsumableArray(args.map(function (a) {
    return formatIndividualArg(a);
  })), [formatIndividualArg(context), window && window.location ? window.location.toString() : 'no-location']).reduce(function (msg, token) {
    return "".concat(msg, " ").concat(token);
  });
}

var RemoteLogClient =
/*#__PURE__*/
function () {
  function RemoteLogClient(config) {
    _classCallCheck(this, RemoteLogClient);

    var _config$context = config.context,
        context = _config$context === void 0 ? {} : _config$context,
        url = config.url,
        _config$maxWaitMs = config.maxWaitMs,
        maxWaitMs = _config$maxWaitMs === void 0 ? 5000 : _config$maxWaitMs,
        _config$maxMessagesPe = config.maxMessagesPerInterval,
        maxMessagesPerInterval = _config$maxMessagesPe === void 0 ? 200 : _config$maxMessagesPe;
    this.context = context;
    this.requestBuilder = new RequestBuilder({
      url: url,
      cache: 'no-cache',
      mode: 'cors'
    }).post().withCookies();
    this.maxMessagesPerInterval = maxMessagesPerInterval;
    this.messages = [];
    this.maxWaitMs = maxWaitMs;
    this.flushNow = this.flushMessagesToServer;
    this.flushMessagesToServer = debounce(this.flushMessagesToServer, maxWaitMs, {
      maxWait: maxWaitMs,
      leading: true
    });
    /** Log requests that are yet to be sent to the server Array<Promise<mixed>> */

    this.pendingLogRequests = [];
  }

  _createClass(RemoteLogClient, [{
    key: "debug",
    value: function debug() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      this.logMessage.apply(this, ['debug', this.context].concat(args));
    }
  }, {
    key: "info",
    value: function info() {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      this.logMessage.apply(this, ['info', this.context].concat(args));
    }
  }, {
    key: "warn",
    value: function warn() {
      for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args[_key3] = arguments[_key3];
      }

      this.logMessage.apply(this, ['warn', this.context].concat(args));
    }
  }, {
    key: "error",
    value: function error() {
      for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
        args[_key4] = arguments[_key4];
      }

      this.logMessage.apply(this, ['error', this.context].concat(args));
    }
    /**
     * Log a message.
     * @param {('debug' | 'info' | 'warn' | 'error')} logLevel - level string
     * @param {Object} context - the context
     * @param {String} marker - marker string
     * @param {String} message - message
     * @param  {...any} args - remaining args as an array
     */

  }, {
    key: "logMessage",
    value: function logMessage(logLevel, context, marker, message) {
      for (var _len5 = arguments.length, args = new Array(_len5 > 4 ? _len5 - 4 : 0), _key5 = 4; _key5 < _len5; _key5++) {
        args[_key5 - 4] = arguments[_key5];
      }

      var postData = {
        logLevel: logLevel,
        marker: marker,
        message: formatMessage(marker, message, args, context)
      };

      if (this.messages.length >= this.maxMessagesPerInterval) {
        var max = this.maxMessagesPerInterval;
        var wait = this.maxWaitMs;
        this.messages[this.maxMessagesPerInterval - 1] = {
          logLevel: 'warn',
          marker: 'RemoteLogClient',
          message: "[".concat(marker, "]: exceeded max number of log messages ").concat(max, " per ").concat(wait, " (MS)")
        };
      } else {
        this.messages.push(postData);
      }

      this.flushMessagesToServer();
    } // flush pending messages to the server, this method is throttled
    // see constructor.

  }, {
    key: "flushMessagesToServer",
    value: function flushMessagesToServer() {
      var _this = this;

      if (!this.messages.length) {
        // if a call to flushNow happened after the last call to this method
        // then the debouncing could result in an empty array
        return Promise.all(this.pendingLogRequests);
      }

      var request = this.requestBuilder.json(this.messages).build();
      var count = this.messages.length;
      this.messages.length = 0;
      var pendingLogRequest = fetch(request).then(function (response) {
        _this.pendingLogRequests = _this.pendingLogRequests.filter(function (item) {
          return item !== pendingLogRequest;
        });

        if (!response.ok) {
          _this.error('RemoteLogClient', 'failed to log messages %s: %s. Request payload: %s.', response.status, response.statusText, request);
        }
      }).catch(function (e) {
        _this.pendingLogRequests = _this.pendingLogRequests.filter(function (item) {
          return item !== pendingLogRequest;
        });
        var errorMessage = e & e.message ? e.message : e;

        _this.error('RemoteLogClient', 'failed to log messages %d  %s', count, errorMessage);
      });
      this.pendingLogRequests.push(pendingLogRequest);
      return pendingLogRequest;
    }
  }]);

  return RemoteLogClient;
}();

export default RemoteLogClient;
//# sourceMappingURL=index.js.map
